import React, {useRef, useState} from "react";
import {Box, Form, FormField, Text, TextInput} from "grommet";
import {useRouter} from "next/router";
import OAuthManager, {OAUTH_CALLBACK_PATH} from "./OAuthManager";
import Shopify from "./icons/Shopify";
import CustomIcon from "./CustomIcon";
import {handleErrors} from "../utils/errors";
import StatusMessage from "./StatusMessage";
import Button from "./Button";

const LinkShopify = ({ userId, onLinked, ctaColor, buttonWrapperProps, vertical = false, refreshPage = false }) => {
    const [isOauthWindowOpen, setIsOauthWindowOpen] = useState(false);
    const [oauthError, setOauthError] = useState();
    const oAuthManagerRef = useRef();
    const [shopName, setShopName] = useState('');
    const router = useRouter();

    const onSubmit = () => {
        fetch('/api/signup/shop', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ shop: shopName, user_id: userId, success_path: OAUTH_CALLBACK_PATH }),
        })
            .then(handleErrors)
            .catch(setOauthError)
            .then((data) => {
                if (data?.auth_url) {
                    oAuthManagerRef.current.showAuthDialog(data.auth_url);
                }
            });
    };

    const onComplete = () => {
        setIsOauthWindowOpen(false);
        onLinked?.();
        if (refreshPage) {
            router.reload();
        }
    };

    const onError = (err) => {
        setIsOauthWindowOpen(false);
        setOauthError(err);
    };

    const onDismiss = () => {
        setIsOauthWindowOpen(false);
        setOauthError(undefined);
    };

    const onOpen = () => {
        setOauthError(undefined);
        setIsOauthWindowOpen(true);
    };

    const isValid = /^[\w\-_]+$/.test(shopName);

    return (
        <Box>
            <OAuthManager
                ref={oAuthManagerRef}
                onComplete={onComplete}
                onError={onError}
                onDismiss={onDismiss}
                onOpen={onOpen}
            />
            {oauthError && <StatusMessage apiError={oauthError} condensed fontSize="xsmall" />}

            <Form onSubmit={onSubmit}>
                <FormField
                    name="shop"
                    error={!isValid && shopName ? 'Please enter a valid shop name' : undefined}
                    info={vertical && shopName ? `${shopName}.myshopify.com` : undefined}
                >
                    {vertical ? (
                        <TextInput
                            name="shop"
                            required={{indicator: false}}
                            placeholder="acmeshopping"
                            value={shopName}
                            onChange={e => setShopName(e.target.value)}
                            data-testid="shopify-form-shop"
                            focusIndicator={false}
                            plain
                        />
                    ) : (
                        <Box direction="row" align="center" pad={{ horizontal: 'xsmall' }}>
                            <Text color="text-xweak">https://</Text>
                            <TextInput
                                name="shop"
                                placeholder="acmeshopping"
                                value={shopName}
                                onChange={e => setShopName(e.target.value)}
                                data-testid="shopify-form-shop"
                                focusIndicator={false}
                                plain
                            />
                            <Text color="text-xweak">.myshopify.com</Text>
                        </Box>
                    )}
                </FormField>

                <Box pad={{ vertical: 'medium', horizontal: 'none' }} {...buttonWrapperProps}>
                    <Button
                        event={{ section: 'link_shopify', element: 'submit' }}
                        type="submit"
                        icon={<CustomIcon SelectedIcon={Shopify} color="text" size="medium" />}
                        primary
                        reverse
                        label={<Text color="text">Connect</Text>}
                        disabled={isOauthWindowOpen || !!(shopName && !isValid)}
                        data-testid="shopify-form-submit"
                        {...(ctaColor ? { color: ctaColor } : {})}
                    />
                </Box>
            </Form>
        </Box>
    );
};

export default LinkShopify;
